import React from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useHotel } from "../contexts/HotelContext";

interface ModeratorRouteProps {
  children: React.ReactNode;
}

/**
 * KiedyNabory moderator route guard.
 *
 * Allows access to users with one of:
 *  - global admin role (`user.role === "admin"`)
 *  - `admin:kiedynabory:write` module on any of their userTenant memberships
 *    (wildcards `admin:kiedynabory:*` and `admin:*` also match)
 *
 * Scope: KiedyNabory-specific (current single moderator role = Paulina).
 * When a second moderator role is introduced (e.g. hotel domain), consider
 * extracting a parametrized `<RoleGuard predicate={...}>` (rule of three).
 *
 * @see apps/backend/src/grants/guards/grants-admin.guard.ts (GrantsAdminOrModeratorGuard) — backend mirror
 */
const ModeratorRoute: React.FC<ModeratorRouteProps> = ({ children }) => {
  const { user, isLoading, hasModuleEnabled } = useHotel();
  const location = useLocation();

  // Show loading while checking authentication and user data
  if (isLoading) {
    return (
      <div
        data-testid="moderator-route-loading"
        className="min-h-screen bg-neutral-50 flex items-center justify-center"
      >
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto mb-4"></div>
          <p className="text-neutral-600">Ładowanie...</p>
        </div>
      </div>
    );
  }

  // Allow global admins OR users with admin:kiedynabory:write module
  if (!user || (user.role !== "admin" && !hasModuleEnabled("admin:kiedynabory:write"))) {
    return <Navigate to="/" state={{ from: location }} replace />;
  }

  return <>{children}</>;
};

export default ModeratorRoute;
